import "core-js";
import "whatwg-fetch";
import "abortcontroller-polyfill/dist/polyfill-patch-fetch";
// Order matters: AbortController needs fetch which needs Promise (provided by core-js).
import "md-gum-polyfill";

import Uppy from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import Dashboard from "@uppy/dashboard";
import AwsS3 from "@uppy/aws-s3";
import {
    registerStereoAudioPreProcessor,
    isStereoValidationFailureError
} from "../shared/stereo_audio_preprocessor";
import {
    AUDIO_ALLOWED_FILE_TYPES,
    AUDIO_ALLOWED_FILE_TYPES_MESSAGE
} from "../shared/audio_upload_options";
import { getEpisodeAudioChannelValidationOptions } from "../shared/episode_audio_channel_validation_options";
import {
    getEpisodeNumberValidationError,
    isEpisodeNumberValidationMessage,
    isRichTextBlank
} from "./shared/audio_episode_form_validation";
import { navigateToVideoSetup } from "./shared/video_setup_navigation";

import { checkProcessingWarning, convertProcessingToDraft, cancelProcessingWarning } from "./shared/processing_warning_utils";

require("@uppy/core/dist/style.min.css");
require("@uppy/dashboard/dist/style.min.css");

document.addEventListener("alpine:init", () => {
    Alpine.data("new_edit_episode_x_data", () => ({
        init() {
            uppy_episode_audio(this);
            uppy_cover_art(this);
            externalJS(this);
            this.set_data();
            this.check_audio_conversion_status();
            this.check_success_modal();
            checkProcessingWarning(this);
        },
        audio_upload: false,
        cover_art_upload: false,
        cover_art_error_msg: "",
        save_text: "",
        audio_error: "",
        title_error: "",
        description_error: "",
        season_error: "",
        episode_number_error: "",
        episode_errors: [],
        schedule_error: "",
        /**
         * Keeps title validation beside its field and all other errors in the
         * existing page summary.
         *
         * @param {Object|Array|string} reason
         * @returns {void}
         */
        apply_submission_errors(reason) {
            if (!reason || Array.isArray(reason) || typeof reason !== "object") {
                this.title_error = "";
                this.episode_errors = reason || [];
                return;
            }

            const remainingErrors = { ...reason };
            const titleErrors = remainingErrors.title;
            delete remainingErrors.title;

            this.title_error = Array.isArray(titleErrors) ? (titleErrors[0] || "") : (titleErrors || "");
            this.episode_errors = Object.keys(remainingErrors).length === 0 ? [] : remainingErrors;
        },
        audio_publishing_action: "draft",
        original_audio_file_name: "",
        audio_file_name: "",
        draft_text: "Save as Draft",
        draft_disabled: false,
        submit_disabled: false,
        continue_to_video_text: "Go to video set-up",
        continue_to_video_disabled: false,
        background_upload_text: "",
        replace_audio_clicked: false,
        keyword_exists: false,
        contributor_exists: false,
        cover_art_assembly_id: "",
        original_pub_date: "",
        pub_date_error: "",
        show_hide_from_feed_modal: false,
        show_appear_on_rss_feed_modal: false,
        download_audio_fn: null,
        // Success modal for youtube_and_audio episodes
        success_modal: false,
        modal_header: '',
        modal_body: '',
        modal_secondary_body: '',
        modal_actions: [],
        // Audio conversion in progress flag
        audio_conversion_in_progress: false,
        // Episode state flags
        isScheduled: false,
        // Conversion confirmation modal state
        audio_conversion_modal: false,
        conversion_action_type: '', // 'publish' or 'schedule'
        pending_submission_data: null,
        // Processing warning modal state
        processing_warning_modal: false,
        is_converting_to_draft: false,
        check_audio_conversion_status() {
            // Check if audio conversion is in progress from episode data
            const dataEl = document.getElementById("ql-data-points");
            const episodeId = dataEl.dataset.episodeId;

            // This would be set from the server-side rendered data
            // We'll check if the banner element exists as a proxy for conversion status
            const conversionBanner = document.querySelector('[x-ref="audio_conversion_banner"]');
            this.audio_conversion_in_progress = conversionBanner !== null;

            return this.audio_conversion_in_progress;
        },
        scroll_to_conversion_banner() {
            const banner = document.querySelector('[x-ref="audio_conversion_banner"]');
            if (banner) {
                banner.scrollIntoView({behavior: 'smooth', block: 'start'});
            }
        },
        async check_live_conversion_status() {
            // Make API call to check live conversion status
            const dataEl = document.getElementById("ql-data-points");
            const showId = dataEl.dataset.showId;
            const episodeId = dataEl.dataset.episodeId;

            try {
                const response = await fetch(`/shows/${showId}/episodes/${episodeId}/check-audio-conversion-status`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    }
                });

                const data = await response.json();

                if (data.success) {
                    return data.conversion_in_progress;
                } else {
                    // On error, assume no conversion in progress to allow normal flow
                    console.error('Error checking conversion status:', data.error);
                    return false;
                }
            } catch (error) {
                console.error('Failed to check conversion status:', error);
                // On network error, assume no conversion in progress
                return false;
            }
        },
        set_data() {
            const dataEl = document.getElementById("ql-data-points");
            const cover_art_url = dataEl.dataset.coverArtUrl;
            const scheduled_time = dataEl.dataset.scheduledTime;
            const publishing_intent = dataEl.dataset.publishingIntent;
            const audio_file_name = dataEl.dataset.audioFileName;
            const isPublished = dataEl.dataset.isPublished === "true";
            const isScheduled = dataEl.dataset.isScheduled === "true";
            this.isScheduled = isScheduled;

            this.set_audio_data();

            if (cover_art_url !== "") {
                document.querySelector("#new_cover_art").src = cover_art_url;
                document.querySelector("#episode_cover_art_assembly_id").value =
                    dataEl.dataset.coverArtId;
                this.cover_art_upload = true;
            }

            const applePublishingAction = document.querySelector(
                "[data-apple-audio-publishing-action]"
            );
            if (applePublishingAction && !isPublished) {
                this.set_audio_publishing_action(applePublishingAction.value);
                if (this.audio_publishing_action === "scheduled") {
                    document.querySelector("#app_episode_scheduled_at_time").value =
                        scheduled_time;
                }
            } else {
                if (publishing_intent === "publish_now" && !isPublished) {
                    document.querySelector("#ql-schedule-now").checked = true;
                    this.save_text = "Publish";
                } else if (
                    (publishing_intent === "scheduled" || (isScheduled && scheduled_time !== "")) &&
                    !isPublished
                ) {
                    document.querySelector("#ql-schedule-later").checked = true;
                    document.querySelector("#app_episode_scheduled_at_time").value =
                        scheduled_time;
                    document
                        .querySelector("#ql-schedule-section")
                        .classList.remove("hidden");
                }

                // Preserve legacy labels for published episodes and ordinary Audio.
                if (isPublished) {
                    this.save_text = "Update Episode";
                } else if (publishing_intent === "scheduled") {
                    this.save_text = "Schedule";
                } else if (isScheduled && scheduled_time !== "") {
                    this.save_text = "Update Episode";
                }
            }

            if (audio_file_name !== "") {
                this.original_audio_file_name = audio_file_name;
                this.audio_file_name = audio_file_name;
            }
            this.original_pub_date = dataEl.dataset.pubDate;
        },
        /**
         * Maps the Apple publishing dropdown onto the existing Audio form controls.
         *
         * @param {string} action
         * @returns {void}
         */
        set_audio_publishing_action(action) {
            const scheduleNow = document.querySelector("#ql-schedule-now");
            const scheduleLater = document.querySelector("#ql-schedule-later");
            const scheduleSection = document.querySelector("#ql-schedule-section");
            const scheduleInput = document.querySelector("#app_episode_scheduled_at_time");
            const applePublishingAction = document.querySelector(
                "[data-apple-audio-publishing-action]"
            );

            if (!scheduleNow || !scheduleLater || !scheduleSection) {
                return;
            }

            this.audio_publishing_action = action;
            scheduleNow.checked = action === "publish_now";
            scheduleLater.checked = action === "scheduled";
            scheduleSection.classList.toggle("hidden", action !== "scheduled");
            if (scheduleInput && applePublishingAction) {
                scheduleInput.disabled = action !== "scheduled";
            }
            this.schedule_error = "";

            if (action === "publish_now") {
                this.save_text = "Publish";
            } else if (action === "scheduled") {
                this.save_text = "Schedule";
            } else {
                this.save_text = "";
            }
        },
        set_audio_data() {
            const dataEl = document.getElementById("ql-data-points");
            const audio_url = dataEl.dataset.enclosureUrl;
            if (audio_url !== "") {
                let audio_div = document.getElementById("ql-uploaded-audio-insert");
                // create an event listener to enable us download audio cross-origin audio files
                this.download_audio_fn = downloadAudioHandler(
                    audio_url,
                    dataEl.dataset.audioFileName
                );
                document
                    .getElementById("download-audio-btn")
                    .addEventListener("click", this.download_audio_fn);
                audio_div.setAttribute("src", audio_url);
                document.querySelector("#audio_assembly_id").value =
                    dataEl.dataset.audioId;
                audio_div.load();
                handleAudioDuration();
                this.audio_upload = true;
            }
        },
        handle_hide_from_rss_feed_checkbox_clicked(target_element) {
            target_element.preventDefault();
            if (target_element.target.checked) {
                this.show_hide_from_feed_modal = true;
            } else {
                this.show_appear_on_rss_feed_modal = true;
            }
        },
        handle_hide_from_rss_feed_confirm() {
            document.querySelector("#app_episode_hide_from_feed").checked = true;
            this.show_hide_from_feed_modal = false;
        },
        handle_appear_on_rss_feed_confirm() {
            document.querySelector("#app_episode_hide_from_feed").checked = false;
            this.show_appear_on_rss_feed_modal = false;
        },
        has_valid_inputs(type, skipAudioCheck = false) {
            if (type == "") {
                return false;
            }

            const dataEl = document.getElementById("ql-data-points");
            const audio_url = dataEl.dataset.enclosureUrl;
            const titleField = document.querySelector("#app_episode_title");
            const descriptionField = document.querySelector("#app_episode_description");
            // const audioField = document.querySelector("#audio_assembly_id");
            const audioUploaded = document.querySelector("#audio_uploaded");

            const episodeField = document.querySelector(
                "#episode_number"
            );
            const scheduleField = document.querySelector(
                "#app_episode_scheduled_at_time"
            );
            const scheduleLaterField = document.querySelector("#ql-schedule-later");
            const pubDateField = document.querySelector("#app_episode_pub_date");
            const episodeNumberError = getEpisodeNumberValidationError(episodeField?.value);

            let found_error = false;

            this.description_error = "";
            if (isEpisodeNumberValidationMessage(this.episode_number_error)) {
                this.episode_number_error = "";
            }

            if (type == "draft") {
                console.log(titleField.value == "");
                if (titleField.value == "") {
                    this.title_error = "Please enter a title";
                    titleField.scrollIntoView({behavior: "smooth", block: "center"});
                    found_error = true;
                }
            } else if (type == "submit") {
                if (titleField.value == "") {
                    this.title_error = "Please enter a title";
                    found_error = true;
                    titleField.scrollIntoView({behavior: "smooth", block: "center"});
                } else if (!skipAudioCheck && audioUploaded?.value == "" && audio_url == "") {
                    this.audio_error = "Please upload an audio file";
                    found_error = true;
                    document
                        .querySelector("#audio_section")
                        .scrollIntoView({behavior: "smooth", block: "center"});
                } else if (scheduleLaterField.checked && scheduleField.value == "") {
                    this.schedule_error =
                        "Please select a date to schedule your episode.";
                    scheduleField.scrollIntoView({behavior: "smooth", block: "center"});
                    found_error = true;
                } else if (isRichTextBlank(descriptionField?.value)) {
                    this.description_error = "Please enter a description";
                    document.querySelector(".ql-editor").scrollIntoView({behavior: "smooth", block: "center"});
                    found_error = true;
                } else if (
                    document.getElementById("ql-data-points").dataset.isPublished ===
                    "true" &&
                    pubDateField.value == ""
                ) {
                    // Basically just takes out the seconds and timezone
                    // const dateString = "2024-02-28 13:10:14 UTC";
                    //
                    // const [datePart, timePart] = dateString.split(' ');
                    // const [year, month, day] = datePart.split('-');
                    // const [hours, minutes] = timePart.split(':');
                    //
                    // const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}`;
                    document
                        .querySelector("#app_episode_pub_date")
                        ._flatpickr.setDate(this.original_pub_date);

                    this.pub_date_error = `Publication date cannot be empty or we will reset it to the original update. Please choose a publication date.`;
                    pubDateField.scrollIntoView({behavior: "smooth", block: "center"});
                    found_error = true;
                }
            }

            if (episodeNumberError !== "") {
                this.episode_number_error = episodeNumberError;
                episodeField?.scrollIntoView({behavior: "smooth", block: "center"});
                found_error = true;
            }

            if (found_error) {
                return false;
            } else {
                return true;
            }
        },
        async handle_submit() {
            const that = this;

            // Check if this is an update to a published or scheduled episode
            const isPublished = document.getElementById("ql-data-points").dataset.isPublished === "true";
            const isScheduled = document.getElementById("ql-data-points").dataset.isScheduled === "true";

            // Determine if user is trying to publish or schedule
            const isPublishingNow = document.querySelector("#ql-schedule-now").checked;
            const isSchedulingLater = document.querySelector("#ql-schedule-later").checked;
            const isPublishOrSchedule = isPublishingNow || isSchedulingLater;

            // Only check live conversion status if trying to publish/schedule
            if (isPublishOrSchedule && !isPublished) {
                // Perform live check for conversion status
                const conversionInProgress = await this.check_live_conversion_status();

                if (conversionInProgress) {
                    // Store submission data for later use
                    const form = document.querySelector(".edit_app_episode");
                    const data = new FormData(form);
                    this.pending_submission_data = new URLSearchParams(data);

                    // Set action type for modal display
                    this.conversion_action_type = isPublishingNow ? 'publish' : 'schedule';

                    // Show conversion confirmation modal
                    this.audio_conversion_modal = true;
                    return;
                }
            }

            // For published/scheduled episodes during conversion, still use old check for blocking
            if ((isPublished || isScheduled) && this.check_audio_conversion_status()) {
                this.scroll_to_conversion_banner();
                return;
            }

            const form = document.querySelector(".edit_app_episode");
            const data = new FormData(form);
            const submitData = new URLSearchParams(data);
            let submit_type;

            // For published episodes, always treat as submit unless they're being reverted to draft
            if (isPublished) {
                submit_type = "submit";
                // Published updates need to carry the existing keep-published intent.
                if (
                    submitData.get("app_episode[is_draft]") !== "true" &&
                    !submitData.has("app_episode[publish_now]")
                ) {
                    submitData.append("app_episode[publish_now]", "true");
                }
            } else if (
                document.querySelector("#ql-schedule-now").checked ||
                document.querySelector("#ql-schedule-later").checked
            ) {
                submit_type = "submit";
            } else {
                submit_type = "draft";
                submitData.append("app_episode[is_draft]", "true");
            }

            if (this.has_valid_inputs(submit_type)) {
                // Disable all buttons during submission
                this.draft_disabled = true;
                this.submit_disabled = true;
                this.continue_to_video_disabled = true;

                if (submit_type == "draft") {
                    this.save_text = "Updating Episode...";
                } else {
                    // For published episodes being updated
                    if (isPublished) {
                        this.save_text = "Updating Episode...";
                    } else if (submitData.get("app_episode[publish_now]") === "true") {
                        this.save_text = "Publishing Episode...";
                    } else {
                        this.save_text = "Scheduling Episode...";
                    }
                }

                submitData.append("app_episode[audio_file_name]", that.audio_file_name);
                (async () => {
                    const rawResponse = await fetch(form.action, {
                        method: "PATCH",
                        body: submitData,
                    });
                    const response = await rawResponse.json();

                    if (response.status === "success") {
                        window.location.href = response.link;
                    } else if (response.status === "error") {
                        that.apply_submission_errors(response.reason);
                        // Re-enable buttons on error
                        that.draft_disabled = false;
                        that.submit_disabled = false;
                        that.continue_to_video_disabled = false;

                        // Reset button text based on episode state
                        if (isPublished) {
                            that.save_text = "Update Episode";
                        } else if (submitData.get("app_episode[publish_now]") === "true") {
                            that.save_text = "Publish";
                        } else if (submitData.get("app_episode[scheduled_at_time]")) {
                            that.save_text = "Schedule";
                        } else {
                            that.save_text = "Update Episode";
                        }
                        that.$refs.episode_errors.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                })();
            } else {
                document.querySelector(".ql-submit").classList.add("border-red-400");
            }
        },
        handle_draft() {
            const that = this;

            // Check if this is a published or scheduled episode
            const isPublished = document.getElementById("ql-data-points").dataset.isPublished === "true";
            const isScheduled = document.getElementById("ql-data-points").dataset.isScheduled === "true";

            // If audio conversion is in progress and episode is published/scheduled, block the action
            if (this.check_audio_conversion_status() && (isPublished || isScheduled)) {
                this.scroll_to_conversion_banner();
                return;
            }

            const form = document.querySelector(".edit_app_episode");
            const data = new FormData(form);
            const submitData = new URLSearchParams(data);
            console.log("HI");
            console.log(this.has_valid_inputs("draft"));
            if (this.has_valid_inputs("draft")) {
                // Disable all buttons during submission
                this.draft_disabled = true;
                this.submit_disabled = true;
                this.continue_to_video_disabled = true;

                submitData.append("app_episode[is_draft]", "true");
                submitData.append("app_episode[audio_file_name]", that.audio_file_name);
                this.draft_text = "Saving...";
                (async () => {
                    const rawResponse = await fetch(form.action, {
                        method: "PATCH",
                        body: submitData,
                    });
                    const response = await rawResponse.json();

                    if (response.status === "success") {
                        window.location.href = response.link;
                    } else if (response.status === "error") {
                        that.apply_submission_errors(response.reason);
                        // Re-enable buttons on error
                        that.draft_disabled = false;
                        that.submit_disabled = false;
                        that.continue_to_video_disabled = false;
                        this.draft_text = "Save as Draft";
                        that.$refs.episode_errors.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                })();
            } else {
                document
                    .querySelector(".ql-submit-draft")
                    .classList.add("border-red-400");
            }
        },
        handle_save_and_continue(continue_to) {
            const that = this;
            const form = document.querySelector(".edit_app_episode");
            const data = new FormData(form);
            const submitData = new URLSearchParams(data);

            // Add continue parameter to tell controller where to redirect
            submitData.append("continue_to", continue_to);

            // Use basic validation (no specific submit type needed)
            if (this.has_valid_inputs("draft")) {

                // Set loading state based on continue_to direction
                if (continue_to === 'video') {
                    this.continue_to_video_text = "Saving...";
                    this.continue_to_video_disabled = true;
                }

                submitData.append("app_episode[audio_file_name]", that.audio_file_name);

                (async () => {
                    const rawResponse = await fetch(form.action, {
                        method: "PATCH",
                        body: submitData,
                    });
                    const response = await rawResponse.json();

                    if (response.status === "success") {
                        window.location.href = response.link;
                    } else if (response.status === "error") {
                        that.apply_submission_errors(response.reason);
                        // Reset loading state on error
                        if (continue_to === 'video') {
                            that.continue_to_video_text = "Go to video set-up";
                            that.continue_to_video_disabled = false;
                        }
                        that.$refs.episode_errors.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                })();
            } else {
                // Show validation errors
                document.querySelector(".ql-submit").classList.add("border-red-400");
            }
        },
        check_if_cover_art_uploaded() {
            let that = this;
            // Cover art still uploads via Transloadit; notify support if encoding stalls past 60s.
            if (this.cover_art_upload === true) {
                return;
            }

            (async () => {
                const rawResponse = await fetch(
                    `https://api2.transloadit.com/assemblies/${that.cover_art_assembly_id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );
                const content = await rawResponse.json();

                if (
                    content.ok === "ASSEMBLY_UPLOADING" ||
                    content.ok === "ASSEMBLY_EXECUTING"
                ) {
                    that.send_upload_error_notification(that.cover_art_assembly_id);
                }
            })();
        },
        send_upload_error_notification(assembly_id) {
            (async () => {
                const rawResponse = await fetch(
                    document.getElementById("ql-data-points").dataset
                        .uploadErrorNotifyUrl,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            authenticity_token: document.querySelector(
                                'meta[name="csrf-token"]'
                            ).content,
                            assembly_id: assembly_id,
                        }),
                    }
                );
                const content = await rawResponse.json();
            })();
        },
        handle_duplicate_check(event) {
            let that = this;
            const fieldValue = parseInt(event.target.value);

            const episodeSeason = document.getElementById("episode_season").value;
            const episodeNumber = document.getElementById("episode_number").value;

            if (episodeSeason && episodeNumber) {
                const episodeId =
                    document.getElementById("ql-data-points").dataset.episodeId;
                const checkDuplicateUrl = `${
                    document.getElementById("ql-data-points").dataset.checkDuplicateUrl
                }?id=${episodeId}&episode_season=${episodeSeason}&episode_number=${fieldValue}`;
                fetch(checkDuplicateUrl)
                    .then((response) => response.json())
                    .then((data) => {
                        console.log({data});
                        const {duplicate, episode_title} = data;
                        if (duplicate) {
                            that.episode_number_error = `Please note, the episode titled: '${episode_title}' already has this season (${episodeSeason}) and episode number (${episodeNumber})`;
                        } else {
                            that.episode_number_error = "";
                        }
                    });
            } else {
                that.episode_number_error = "";
            }
        },
        go_to_settings_page() {
            const dataEl = document.getElementById("ql-data-points");
            const showId = dataEl.dataset.showId;
            const settingsPage = `/shows/${showId}/distribution/show-settings#cover-art-section`;
            const titleField = document.querySelector("#app_episode_title");
            if (titleField.value === "") {
                document.location.href = settingsPage;
                return;
            }

            const that = this;
            const form = document.querySelector(".edit_app_episode");
            const data = new FormData(form);
            const submitData = new URLSearchParams(data);
            submitData.append("app_episode[is_draft]", "true");
            submitData.append("app_episode[audio_file_name]", that.audio_file_name);
            this.draft_text = "Saving...";
            (async () => {
                const rawResponse = await fetch(form.action, {
                    method: "PATCH",
                    body: submitData,
                });
                const response = await rawResponse.json();
                if (response.status === "success") {
                    setTimeout(function () {
                        document.location.href = settingsPage;
                    }, 250);
                } else if (response.status === "error") {
                    that.apply_submission_errors(response.reason);
                    this.draft_text = "Save as Draft";
                    that.$refs.episode_errors.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            })();
        },
        check_success_modal() {
            // Check URL parameters for success modal
            const urlParams = new URLSearchParams(window.location.search);
            const modal = urlParams.get('modal');
            const action = urlParams.get('submit_action');
            const linked_state = urlParams.get('linked_state');

            if (modal === 'success' && action && linked_state) {
                this.configure_success_modal(action, linked_state);
                this.success_modal = true;

                // Clean up URL parameters
                const newUrl = window.location.pathname;
                window.history.replaceState({}, '', newUrl);
            }
        },
        convert_processing_to_draft() {
            convertProcessingToDraft(this);
        },
        cancel_processing_warning() {
            cancelProcessingWarning();
        },
        configure_success_modal(action, linked_state) {
            // Configure modal content based on action and linked_state
            const isLinkedPublished = linked_state === 'published';

            if (action === 'draft') {
                this.modal_header = 'Draft saved successfully!';
                this.modal_body = 'Your audio episode has been saved as a draft.';
                if (isLinkedPublished) {
                    this.modal_secondary_body = 'Your video episode is published. You can continue editing your audio or exit.';
                    this.modal_actions = [
                        {text: 'Exit', action: 'exit', primary: false},
                        {text: 'Continue to video set-up', action: 'continue_video', primary: true}
                    ];
                } else {
                    this.modal_secondary_body = 'What would you like to do next?';
                    this.modal_actions = [
                        {text: 'Exit', action: 'exit', primary: false},
                        {text: 'Continue to video set-up', action: 'continue_video', primary: true}
                    ];
                }
            } else if (action === 'scheduled') {
                this.modal_header = 'Your audio has been scheduled!';
                this.modal_body = 'Your audio episode has been scheduled for publication.';
                if (isLinkedPublished) {
                    this.modal_header = 'Success! Both audio & video are scheduled/published.';
                    this.modal_body = 'You may now exit the page.';
                    this.modal_secondary_body = '';
                    this.modal_actions = [
                        {text: 'Got it!', action: 'exit', primary: true}
                    ];
                } else {
                    this.modal_secondary_body = 'You have not published your video yet. What would you like to do next?';
                    this.modal_actions = [
                        {text: 'Exit', action: 'exit', primary: false},
                        {text: 'Continue to video set-up', action: 'continue_video', primary: true}
                    ];
                }
            } else if (action === 'published') {
                this.modal_header = 'Your audio has been published!';
                this.modal_body = 'Your audio episode has been published successfully.';
                if (isLinkedPublished) {
                    this.modal_header = 'Success! Both audio & video are published.';
                    this.modal_body = 'You may now exit the page.';
                    this.modal_secondary_body = '';
                    this.modal_actions = [
                        {text: 'Got it!', action: 'exit', primary: true}
                    ];
                } else {
                    this.modal_secondary_body = 'You have not published your video yet. What would you like to do next?';
                    this.modal_actions = [
                        {text: 'Exit', action: 'exit', primary: false},
                        {text: 'Continue to video set-up', action: 'continue_video', primary: true}
                    ];
                }
            }
        },
        handle_success_modal_action(action) {
            this.success_modal = false;

            if (action === 'exit') {
                // Route to view page
                const dataEl = document.getElementById("ql-data-points");
                const showId = dataEl.dataset.showId;
                const episodeId = dataEl.dataset.episodeId;
                window.location.href = `/shows/${showId}/episodes/${episodeId}`;
            } else if (action === 'continue_video') {
                // Route to video edit page
                const dataEl = document.getElementById("ql-data-points");
                navigateToVideoSetup(dataEl);
            }
        },
        async copy_video_details_to_audio() {
            try {
                const dataEl = document.getElementById("ql-data-points");
                const showId = dataEl.dataset.showId;
                const episodeId = dataEl.dataset.episodeId;

                const detailsUrl = dataEl.dataset.copyVideoDetailsUrl ||
                    `/shows/${showId}/episodes/${episodeId}/get-youtube-episode-details`;
                const response = await fetch(detailsUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    }
                });

                const data = await response.json();
                console.log({copied: data})

                if (data.success) {
                    // Copy title
                    const titleField = document.querySelector('#app_episode_title');
                    if (titleField) {
                        titleField.value = data.title || '';
                    }

                    // Copy description to Quill editor
                    if (window.quill && data.description) {
                        window.quill.root.innerHTML = data.description;
                        // Update the hidden field
                        const descriptionField = document.querySelector('#app_episode_description');
                        if (descriptionField) {
                            descriptionField.value = data.description;
                        }
                    }

                    // Copy keywords using TomSelect
                    const keywordsField = document.querySelector('#app_episode_keywords');
                    if (keywordsField && keywordsField.tomselect && data.keywords) {
                        // Clear existing keywords
                        keywordsField.tomselect.clear();
                        // Handle keywords whether they come as array or comma-separated string
                        let keywordsArray;
                        if (Array.isArray(data.keywords)) {
                            keywordsArray = data.keywords;
                        } else if (typeof data.keywords === 'string') {
                            keywordsArray = data.keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword.length > 0);
                        } else {
                            keywordsArray = [];
                        }

                        // Add new keywords
                        keywordsArray.forEach(keyword => {
                            keywordsField.tomselect.addOption({value: keyword, text: keyword});
                            keywordsField.tomselect.addItem(keyword);
                        });
                    }

                    // Copy publishing settings
                    if (data.publish_mode === 'publish_now') {
                        this.set_audio_publishing_action("publish_now");
                    } else if (data.publish_mode === 'scheduled') {
                        this.set_audio_publishing_action("scheduled");

                        // Set scheduled_at time if available, otherwise leave blank
                        const scheduleTimeField = document.querySelector("#app_episode_scheduled_at_time");
                        if (scheduleTimeField) {
                            if (data.scheduled_at) {
                                // Format the datetime for the input field if needed
                                scheduleTimeField.value = data.scheduled_at;
                                // If flatpickr is initialized, set it through flatpickr
                                if (scheduleTimeField._flatpickr) {
                                    scheduleTimeField._flatpickr.setDate(data.scheduled_at);
                                }
                            } else {
                                // Leave the field blank - user will need to pick a time
                                scheduleTimeField.value = "";
                                if (scheduleTimeField._flatpickr) {
                                    scheduleTimeField._flatpickr.clear();
                                }
                            }
                        }
                    }
                    console.log('Video details copied successfully');
                } else {
                    console.error('Failed to copy video details:', data.error);
                }
            } catch (error) {
                console.error('Error copying video details:', error);
            }
        },
        confirm_publish_with_conversion() {
            // User confirmed they want to publish/schedule with conversion in progress
            const that = this;

            // First validate all fields except audio (since it's converting)
            if (!this.has_valid_inputs("submit", true)) {
                // Validation failed, close modal and show errors
                this.audio_conversion_modal = false;
                return;
            }

            // Close modal
            this.audio_conversion_modal = false;

            // Use stored submission data and add queue flag
            const submitData = this.pending_submission_data;
            submitData.append("queue_publish_after_conversion", "true");

            // Note: We don't add audio_file_name here because it's still converting
            // The backend will handle this when conversion completes

            // Update button text based on action type
            if (this.conversion_action_type === 'publish') {
                this.save_text = "Queueing for publish...";
            } else {
                this.save_text = "Queueing for schedule...";
            }

            // Disable buttons during submission
            this.draft_disabled = true;
            this.submit_disabled = true;
            this.continue_to_video_disabled = true;

            const form = document.querySelector(".edit_app_episode");

            (async () => {
                const rawResponse = await fetch(form.action, {
                    method: "PATCH",
                    body: submitData,
                });
                const response = await rawResponse.json();

                if (response.status === "success") {
                    window.location.href = response.link;
                } else if (response.status === "error") {
                    that.apply_submission_errors(response.reason);
                    // Re-enable buttons on error
                    that.draft_disabled = false;
                    that.submit_disabled = false;
                    that.continue_to_video_disabled = false;

                    // Reset button text
                    if (that.conversion_action_type === 'publish') {
                        that.save_text = "Publish";
                    } else {
                        that.save_text = "Schedule";
                    }

                    that.$refs.episode_errors.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            })();
        },
        cancel_conversion_modal() {
            // User chose to do it later
            this.audio_conversion_modal = false;
            this.pending_submission_data = null;
            this.conversion_action_type = '';
        },
    }));
});

// helper function that allows us download cross-origin audio files as blob
function downloadAudioHandler(url, fileName) {
    return function (event) {
        event.preventDefault();
        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.blob();
            })
            .then((blob) => {
                const link = document.createElement("a");
                const blobUrl = URL.createObjectURL(blob);
                link.href = blobUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            })
            .catch((error) =>
                console.error("There was an error with the download:", error)
            );
    };
}

// Episode cover art still uses Transloadit on the new upload edit page (audio uses S3).
function uppy_cover_art(alpine) {
    let signature = document.getElementById("ql-data-points").dataset.signature;
    let template_episode_cover_art_id =
        document.getElementById("ql-data-points").dataset.templateEpisodeCoverArt;

    const uppy = new Uppy({
        id: "uppy_cover_art",
        debug: true,
        autoProceed: true,
        allowMultipleUploadBatches: false,
        restrictions: {
            maxFileSize: 2 * 1024 * 1024,
            minFileSize: 1,
            maxTotalFileSize: 2 * 1024 * 1024,
            maxNumberOfFiles: 1,
            minNumberOfFiles: 1,
            allowedFileTypes: ["image/*"],
            requiredMetaFields: [],
        },
        locale: {
            strings: {
                youCanOnlyUploadFileTypes: "You can only upload image files",
            },
        },
    });

    uppy.use(Dashboard, {
        trigger: document.getElementById("episode-cover-art-uppy-trigger"),
        showProgressDetails: true,
        note: "Image must be exactly 3000px by 3000px.",
    });

    // Validate image dimensions using Uppy's file-added event
    uppy.on('file-added', (file) => {
        // Only validate image files
        if (!file.type || !file.type.startsWith('image/')) {
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file.data);

        reader.onload = () => {
            const img = new Image();

            img.onload = () => {
                // Validate dimensions - must be exactly 3000x3000
                if (img.width !== 3000 || img.height !== 3000) {
                    // Remove the invalid file
                    uppy.removeFile(file.id);

                    // Set user-friendly error message with actual dimensions
                    alpine.cover_art_error_msg = `You've uploaded an image that is ${img.width}x${img.height}px. We only accept images that are 3000x3000px.`;

                    // Close the modal
                    setTimeout(() => {
                        uppy.getPlugin('Dashboard').closeModal();
                    }, 100);
                } else {
                    // Clear error if dimensions are valid
                    alpine.cover_art_error_msg = "";
                }
            };

            img.onerror = () => {
                uppy.removeFile(file.id);
                alpine.cover_art_error_msg = "Unable to validate image dimensions. Please try a different image.";
                setTimeout(() => {
                    uppy.getPlugin('Dashboard').closeModal();
                }, 100);
            };

            img.src = reader.result;
        };

        reader.onerror = () => {
            uppy.removeFile(file.id);
            alpine.cover_art_error_msg = "Unable to read the image file. Please try again.";
            setTimeout(() => {
                uppy.getPlugin('Dashboard').closeModal();
            }, 100);
        };
    });

    uppy.use(Transloadit, {
        service: "https://api2.transloadit.com",
        params: {
            auth: {
                key: signature,
            },
            template_id: template_episode_cover_art_id,
        },
        waitForEncoding: true,
        waitForMetadata: true,
        importFromUploadURLs: false,
        alwaysRunAssembly: false,
    });

    uppy.on("upload-error", (file, error, response) => {
        if (error.isNetworkError) {
            alert(
                "The audio file was not uploading\n\n" +
                "If you are uploading from an office network, please check with your IT department regarding firewall settings. \n\n" +
                "Error: " +
                error.message
            );
        } else {
            // The error object has 2 keys which are important: error.reason, error.msg
            alpine.cover_art_error_msg = error.msg;
        }
    });

    uppy.on("transloadit:assembly-created", (assembly, fileIDs) => {
        alpine.cover_art_assembly_id = assembly.assembly_id;
    });

    uppy.on("transloadit:assembly-executing", (assembly) => {
        setTimeout(function () {
            alpine.check_if_cover_art_uploaded();
        }, 60000);
    });

    uppy.on("complete", (result) => {
        result.successful.forEach((file) => {
            console.log(file);
            console.log(file.response);
            document.querySelector("#new_cover_art").src = file.response.uploadURL;
            document.querySelector("#episode_cover_art_assembly_id").value =
                file.transloadit.assembly;
            alpine.cover_art_upload = true;
        });
        uppy.cancelAll();
        uppy.getPlugin("Dashboard").closeModal();
    });
}

function uppy_episode_audio(alpine) {
    // New upload flow: direct S3 via @uppy/aws-s3 (no Transloadit assembly polling).
    let signature = document.getElementById("ql-data-points").dataset.signature;
    let template_id = document.getElementById("ql-data-points").dataset.template;
    const dataEl = document.getElementById("ql-data-points");
    const audioUploadedInput = document.querySelector("#audio_uploaded");
    let previousAudioUploadedValue = audioUploadedInput ? audioUploadedInput.value : "";
    const channelValidationOptions = getEpisodeAudioChannelValidationOptions(dataEl);

    const uppy = new Uppy({
        id: "uppy_episode_audio",
        debug: true,
        autoProceed: true,
        allowMultipleUploadBatches: false,
        restrictions: {
            maxFileSize: 300 * 1024 * 1024,
            minFileSize: 300 * 1024,
            maxTotalFileSize: 300 * 1024 * 1024,
            maxNumberOfFiles: 1,
            minNumberOfFiles: 1,
            // Keep this list aligned with browser-decodable formats for reliable client-side stereo checks.
            allowedFileTypes: AUDIO_ALLOWED_FILE_TYPES,
            requiredMetaFields: [],
        },
        locale: {
            strings: {
                youCanOnlyUploadFileTypes: AUDIO_ALLOWED_FILE_TYPES_MESSAGE,
            },
        },
    });

    // Preprocessor blocks autoProceed uploads until async stereo validation completes.
    registerStereoAudioPreProcessor(uppy, channelValidationOptions);

    uppy.use(Dashboard, {
        trigger: document.getElementById("content-uppy-trigger"),
        closeAfterFinish: true,
        showProgressDetails: true,
        hideUploadButton: true,
        note: "Audio files should be 300 MB or less.",
    });

    // uppy.use(Transloadit, {
    //     service: 'https://api2.transloadit.com',
    //     params: {
    //         auth: {
    //             key: signature,
    //         },
    //         template_id: template_id
    //     },
    //     waitForEncoding: true,
    //     waitForMetadata: true,
    //     importFromUploadURLs: false,
    //     alwaysRunAssembly: false,
    // })

    const show_id = document.getElementById("ql-data-points").dataset.showId;
    const episode_id =
        document.getElementById("ql-data-points").dataset.episodeId;

    uppy.use(AwsS3, {
        async getUploadParameters(file) {
            // https://uppy.io/docs/aws-s3/#getuploadparametersfile-options
            return fetch(
                `/shows/${show_id}/episodes/${episode_id}/generate-audio-upload-url`,
                {
                    method: "post",
                    headers: {
                        accept: "application/json",
                        "Content-Type": "application/json",
                        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
                            .content,
                    },
                    body: JSON.stringify({
                        content_type: file.type,
                    }),
                }
            )
                .then((response) => {
                    return response.json();
                })
                .then((data) => {
                    if (audioUploadedInput) {
                        previousAudioUploadedValue = audioUploadedInput.value;
                        audioUploadedInput.value = "";
                    }
                    return {
                        method: "PUT",
                        url: data.presigned_url_data[0],
                        fields: {},
                        headers: data.presigned_url_data[1],
                    };
                });
        },
        shouldUseMultipart: false
    });

    uppy.on("upload", (data) => {
        if (audioUploadedInput) {
            previousAudioUploadedValue = audioUploadedInput.value;
            audioUploadedInput.value = "";
        }
        alpine.background_upload_text =
            "Your audio is continuing to upload. To cancel, select Upload and click the X in the bottom right corner.";
    });

    // transloadit event monitoring no longer need here
    // uppy.on('transloadit:assembly-created', (assembly, fileIDs) => {
    //     console.log("Assembly Created:", assembly.assembly_id)
    //     alpine.audio_assembly_id = assembly.assembly_id
    // });
    //
    // uppy.on('transloadit:assembly-executing', (assembly) => {
    //     console.log("Calling Function Created:", assembly.assembly_id)
    //     setTimeout(function () {
    //         alpine.check_if_audio_uploaded();
    //     }, 180000);
    // });

    uppy.on("upload-error", (file, error, response) => {
        console.log("Upload error:", {file, error, response});
        if (isStereoValidationFailureError(error)) {
            if (audioUploadedInput) {
                audioUploadedInput.value = previousAudioUploadedValue;
            }
            return;
        }

        if (audioUploadedInput) {
            audioUploadedInput.value = "";
        }
        if (response && response.status === 403) {
            console.warn("Likely cause: expired presigned URL");
        }

        if (error.isNetworkError) {
            alert(
                "The audio file was not uploading\n\n" +
                "If you are uploading from an office network, please check with your IT department regarding firewall settings. \n\n" +
                "Error: " +
                error.message
            );
        } else {
            // alert("The audio file was not uploaded\n\n" + "Please check your internet connection and try again")
        }
        console.log("error", error.toString());
        console.log("response", response);
    });

    uppy.on("complete", async (result) => {
        for (const file of result.successful) {
            const audio_download_url = await getAudioPreviewLink(file);

            let audio_div = document.getElementById("ql-uploaded-audio-insert");
            audio_div.setAttribute("src", audio_download_url.download_url);
            if (audioUploadedInput) {
                audioUploadedInput.value = audio_download_url.download_url;
                previousAudioUploadedValue = audio_download_url.download_url;
            }

            // remove the event listener to enable us download newly uploaded audio files
            document
                .getElementById("download-audio-btn")
                .removeEventListener("click", alpine.download_audio_fn);
            const downloadLink = document.getElementById("download-audio-btn");
            downloadLink.href = audio_download_url.download_url;
            downloadLink.download = file.name;
            //   document.querySelector("#audio_assembly_id").value =
            //     file.transloadit.assembly;
            audio_div.load();
            handleAudioDuration();
            if (alpine.replace_audio_clicked) {
                alpine.replace_audio_clicked = false;
            }
            alpine.audio_file_name = file.name;
            alpine.background_upload_text = "";
            alpine.audio_upload = true;
        }
        uppy.cancelAll();
        uppy.getPlugin("Dashboard").closeModal();
    });

    async function getAudioPreviewLink(file) {
        return fetch(
            `/shows/${show_id}/episodes/${episode_id}/generate-audio-download-url`,
            {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
                        .content,
                },
                body: JSON.stringify({
                    content_type: file.type,
                }),
            }
        )
            .then((response) => {
                return response.json();
            })
            .then((data) => {
                return data;
            })
            .catch((error) => {
                console.log(error);
            });
    }
}

function handleAudioDuration() {
    const audio_div = document.getElementById("ql-uploaded-audio-insert");
    const time_div = document.getElementById("audio-play-time");
    audio_div.addEventListener("loadedmetadata", () => {
        console.log(audio_div.duration);
        time_div.innerHTML = "00:00/" + convertSeconds(audio_div.duration);
    });
    audio_div.addEventListener("timeupdate", () => {
        time_div.innerHTML =
            convertSeconds(audio_div.currentTime) +
            "/" +
            convertSeconds(audio_div.duration);
    });
}

function convertSeconds(seconds) {
    if (seconds >= 3600) {
        return new Date(seconds * 1000).toISOString().slice(11, 19);
    } else {
        return new Date(seconds * 1000).toISOString().slice(14, 19);
    }
}

function loadScript(url, callback) {
    let script = document.createElement("script");
    script.type = "text/javascript";

    if (script.readyState) {
        //IE
        script.onreadystatechange = function () {
            if (script.readyState === "loaded" || script.readyState === "complete") {
                script.onreadystatechange = null;
                callback();
            }
        };
    } else {
        //Others
        script.onload = function () {
            callback();
        };
    }

    script.src = url;
    document.getElementsByTagName("head")[0].appendChild(script);
}

function externalJS(alpine) {
    loadScript(
        "https://cdn.jsdelivr.net/npm/tom-select@1.7.6/dist/js/tom-select.complete.min.js",
        function () {
            new TomSelect("#app_episode_keywords", {
                plugins: {
                    remove_button: {
                        title: "Remove this tag",
                    },
                },
                persist: false,
                create: true,
                createOnBlur: true,
                render: {
                    no_results: null,
                },
                createFilter: function (input) {
                    const exists = input in this.options;
                    if (exists) {
                        alpine.keyword_exists = true;
                    } else {
                        alpine.keyword_exists = false;
                    }
                    return !exists;
                },
                onBlur: function () {
                    alpine.keyword_exists = false;
                },
            });

            new TomSelect("#app_episode_contributors", {
                plugins: {
                    remove_button: {
                        title: "Remove this tag",
                    },
                },
                persist: false,
                create: true,
                createOnBlur: true,
                render: {
                    no_results: null,
                },
                createFilter: function (input) {
                    const exists = input in this.options;
                    if (exists) {
                        alpine.contributor_exists = true;
                    } else {
                        alpine.contributor_exists = false;
                    }
                    return !exists;
                },
                onBlur: function () {
                    alpine.contributor_exists = false;
                },
            });
        }
    );

    const scheduleField = document.querySelector("#app_episode_scheduled_at_time");
    if (!scheduleField.dataset.episodeSchedulePicker) {
        flatpickr(scheduleField, {
            enableTime: true,
            minDate: Date.now(),
        });
    }
    if (
        document.getElementById("ql-data-points").dataset.isPublished === "true"
    ) {
        const publicationDateField = document.querySelector("#app_episode_pub_date");
        if (!publicationDateField.dataset.episodeSchedulePicker) {
            flatpickr(publicationDateField, {
                enableTime: true,
                defaultDate: document.getElementById("ql-data-points").dataset.pubDate,
                maxDate: Date.now(),
            });
        }
    }

    const scheduleSection = document.querySelector("#ql-schedule-section");

    document
        .querySelector("#ql-schedule-later")
        .addEventListener("click", function () {
            scheduleSection.classList.remove("hidden");
        });

    document
        .querySelector("#ql-schedule-now")
        .addEventListener("click", function () {
            scheduleSection.classList.add("hidden");
        });

    let toolbar_options = [
        ["bold", "italic"],
        [{list: "ordered"}, {list: "bullet"}],
        ["link"],
        ["undo"],
        ["redo"],
        [{direction: "rtl"}],
        ["clean"],
    ];

    let icons = Quill.import("ui/icons");
    icons["undo"] = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
    <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
  </svg>`;
    icons["redo"] = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
    <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
  </svg>`;

    Quill.register("modules/counter", function (quill, options) {
        let container = document.querySelector(options.container);
        quill.on("text-change", function () {
            let text = quill.getText();
            let words = text.split(/\s+/).length - 1;
            let characters = text.length - 1;

            if (characters > 4000) {
                container.innerHTML = `<span class="text-red-400 font-bold">Reached limit of ${characters} characters for Apple description cutoff</span>`;
            } else if (characters === 0) {
                container.innerHTML = "";
            } else {
                container.innerHTML = `${words} words, ${characters} characters`;
            }
        });
    });
    // Initialize Quill editor
    window.quill = new Quill(".ql-editor", {
        theme: "snow",
        placeholder: "Your episode description...",
        modules: {
            toolbar: {
                container: toolbar_options,
                handlers: {
                    undo: function (value) {
                        this.quill.history.undo();
                    },
                    redo: function (value) {
                        this.quill.history.redo();
                    },
                    link: function (value) {
                        if (value) {
                            var href = prompt("Enter the URL", "https://");
                            this.quill.format("link", href);
                        } else {
                            this.quill.format("link", false);
                        }
                    },
                },
            },
            history: {
                delay: 2000,
                maxStack: 500,
                userOnly: true,
            },
            counter: {
                container: "#counter",
            },
        },
    });

    let description = document.getElementById("app_episode_description");
    window.quill.root.innerHTML = description.value;

    window.quill.on("editor-change", function () {
        description.value = window.quill.root.innerHTML;
    });

    document.addEventListener("turbo:load load", function () {
        let description = document.getElementById("app_episode_description");
        console.log("description", description.value);
        window.quill.root.innerHTML = description.value;
    });
}
